import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Workflow, Check, X, ArrowLeft, FileCode, User, ShieldCheck, XCircle,
  Undo2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const VIEWS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "withdrawn", label: "Withdrawn" },
  { key: "all", label: "All" },
];

const ENTITY_TYPES = [
  { k: "make", label: "Vehicle Make" },
  { k: "model", label: "Vehicle Model" },
  { k: "service_task", label: "Service Task" },
  { k: "translation", label: "Translation Key" },
];

const ACTION_TONE = {
  create: "bg-[#12B76A]/10 text-[#12B76A]",
  update: "bg-[#F79009]/10 text-[#F79009]",
  delete: "bg-[#e63946]/10 text-[#e63946]",
};

const STATUS_TONE = {
  pending: "bg-[#F79009]/10 text-[#F79009]",
  approved: "bg-[#12B76A]/10 text-[#12B76A]",
  rejected: "bg-[#e63946]/10 text-[#e63946]",
  withdrawn: "bg-[#66708515] text-[#667085]",
};

const PillWithTone = ({ v, map }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[v] || "bg-[#66708515] text-[#667085]"}`}>
    {v?.replace(/_/g, " ")}
  </span>
);

export function AdminChangeRequests() {
  const { user: me } = useAuth();
  const canPropose = ["platform_owner", "master_data_admin", "translation_admin"].includes(me?.admin_role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("pending");
  const [entityF, setEntityF] = useState("");
  const [makes, setMakes] = useState([]);

  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposal, setProposal] = useState({
    entity_type: "make", action: "create",
    payload_name: "", payload_make_id: "", payload_hours_est: 0.5, payload_labor_rate: 400,
    payload_category: "", payload_key: "", payload_en: "", payload_hi: "",
    payload_active: true, justification: "",
  });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (view !== "all") params.status_f = view;
    if (entityF) params.entity_type = entityF;
    api.get("/admin/change-requests", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [view, entityF]);

  useEffect(() => {
    api.get("/admin/master-data/makes").then((r) => setMakes(r.data || [])).catch(() => {});
  }, []);

  const submitProposal = async () => {
    let payload = {};
    if (proposal.entity_type === "make") {
      if (!proposal.payload_name.trim()) { toast.error("Name required"); return; }
      payload = { name: proposal.payload_name.trim(), active: !!proposal.payload_active };
    } else if (proposal.entity_type === "model") {
      if (!proposal.payload_name.trim() || !proposal.payload_make_id) { toast.error("Make + name required"); return; }
      payload = { make_id: proposal.payload_make_id, name: proposal.payload_name.trim(), active: !!proposal.payload_active };
    } else if (proposal.entity_type === "service_task") {
      if (!proposal.payload_name.trim()) { toast.error("Name required"); return; }
      payload = {
        name: proposal.payload_name.trim(),
        hours_est: Number(proposal.payload_hours_est) || 0,
        labor_rate: Number(proposal.payload_labor_rate) || 0,
        category: proposal.payload_category || "",
        active: !!proposal.payload_active,
      };
    } else if (proposal.entity_type === "translation") {
      if (!proposal.payload_key.trim() || !proposal.payload_en.trim()) { toast.error("Key + English required"); return; }
      payload = {
        key: proposal.payload_key.trim(), module: "ui",
        en: proposal.payload_en, hi: proposal.payload_hi || "",
      };
    }
    setSaving(true);
    try {
      await api.post("/admin/change-requests", {
        entity_type: proposal.entity_type,
        action: "create",
        payload,
        justification: proposal.justification.trim(),
      });
      toast.success("Change request submitted for review");
      setProposeOpen(false);
      setProposal({
        entity_type: "make", action: "create",
        payload_name: "", payload_make_id: "", payload_hours_est: 0.5, payload_labor_rate: 400,
        payload_category: "", payload_key: "", payload_en: "", payload_hi: "",
        payload_active: true, justification: "",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-change-requests-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Governance</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Workflow size={22} className="text-[#457b9d]" /> Master-data change requests
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Proposed changes queue here for review. Only Platform Owner or Platform Admin can approve.
          </p>
        </div>
        {canPropose && (
          <Dialog open={proposeOpen} onOpenChange={setProposeOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="admin-change-requests-new"
                className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
              >
                <Plus size={14} className="mr-1.5" /> Propose change
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#1d3557] font-semibold">Propose master-data change</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Entity</Label>
                  <select
                    data-testid="admin-cr-entity-select"
                    value={proposal.entity_type}
                    onChange={(e) => setProposal((p) => ({ ...p, entity_type: e.target.value }))}
                    className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                  >
                    {ENTITY_TYPES.map((e) => <option key={e.k} value={e.k}>{e.label}</option>)}
                  </select>
                  <div className="text-[11px] text-[#667085] mt-1">Action: <strong>create</strong> — update/delete flows are still available directly for approvers.</div>
                </div>

                {(proposal.entity_type === "make" || proposal.entity_type === "model" || proposal.entity_type === "service_task") && (
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Name</Label>
                    <Input
                      data-testid="admin-cr-name"
                      value={proposal.payload_name}
                      onChange={(e) => setProposal((p) => ({ ...p, payload_name: e.target.value }))}
                      className="mt-1 rounded-md"
                    />
                  </div>
                )}
                {proposal.entity_type === "model" && (
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Make</Label>
                    <select
                      data-testid="admin-cr-make-select"
                      value={proposal.payload_make_id}
                      onChange={(e) => setProposal((p) => ({ ...p, payload_make_id: e.target.value }))}
                      className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                    >
                      <option value="">Select make…</option>
                      {makes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                {proposal.entity_type === "service_task" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Hours</Label>
                      <Input
                        data-testid="admin-cr-hours"
                        type="number" step="0.25"
                        value={proposal.payload_hours_est}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_hours_est: e.target.value }))}
                        className="mt-1 rounded-md font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Labor ₹</Label>
                      <Input
                        data-testid="admin-cr-rate"
                        type="number" step="50"
                        value={proposal.payload_labor_rate}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_labor_rate: e.target.value }))}
                        className="mt-1 rounded-md font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Category</Label>
                      <Input
                        data-testid="admin-cr-category"
                        value={proposal.payload_category}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_category: e.target.value }))}
                        className="mt-1 rounded-md"
                      />
                    </div>
                  </div>
                )}
                {proposal.entity_type === "translation" && (
                  <>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Key</Label>
                      <Input
                        data-testid="admin-cr-tr-key"
                        value={proposal.payload_key}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_key: e.target.value }))}
                        className="mt-1 rounded-md font-mono"
                        placeholder="e.g. dashboard.welcome"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">English</Label>
                      <Textarea
                        data-testid="admin-cr-tr-en"
                        rows={2}
                        value={proposal.payload_en}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_en: e.target.value }))}
                        className="mt-1 rounded-md"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">हिन्दी</Label>
                      <Input
                        data-testid="admin-cr-tr-hi"
                        value={proposal.payload_hi}
                        onChange={(e) => setProposal((p) => ({ ...p, payload_hi: e.target.value }))}
                        className="mt-1 rounded-md"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Justification</Label>
                  <Textarea
                    data-testid="admin-cr-justification"
                    rows={2}
                    value={proposal.justification}
                    onChange={(e) => setProposal((p) => ({ ...p, justification: e.target.value }))}
                    className="mt-1 rounded-md"
                    placeholder="Why should this be added?"
                  />
                </div>
                <Button
                  onClick={submitProposal}
                  disabled={saving}
                  data-testid="admin-cr-submit"
                  className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
                >
                  {saving ? "Submitting…" : "Submit for review"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E7EC] pb-3">
        <div className="inline-flex gap-1">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`admin-cr-view-${v.key}`}
              className={`h-9 px-3 text-[13px] font-semibold rounded-md transition-colors ${
                view === v.key
                  ? "bg-[#1d3557] text-white"
                  : "bg-white text-[#667085] border border-[#E4E7EC] hover:border-[#457b9d]"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <select
          value={entityF}
          onChange={(e) => setEntityF(e.target.value)}
          data-testid="admin-cr-entity-filter"
          className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
        >
          <option value="">All entities</option>
          {ENTITY_TYPES.map((e) => <option key={e.k} value={e.k}>{e.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-change-requests-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Change</th>
              <th className="text-left px-4 py-3 font-semibold">Proposer</th>
              <th className="text-left px-4 py-3 font-semibold">Created</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {loading && <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                No change requests in this view.
              </td></tr>
            )}
            {items.map((cr) => (
              <tr key={cr.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-cr-row-${cr.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <PillWithTone v={cr.action} map={ACTION_TONE} />
                    <span className="text-[13px] text-[#1d3557] font-semibold uppercase tracking-wider text-[11px]">
                      {cr.entity_type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-[12px] text-[#1d3557] mt-1 truncate max-w-md">
                    {cr.payload?.name || cr.payload?.key || cr.target_id || "—"}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-[12px] text-[#1d3557] font-semibold">{cr.proposer_name || cr.proposer_email}</div>
                  <div className="text-[11px] text-[#457b9d] uppercase tracking-wider font-semibold">
                    {cr.proposer_admin_role?.replace(/_/g, " ")}
                  </div>
                </td>
                <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">{fmtDateTime(cr.created_at)}</td>
                <td className="px-4 py-3"><PillWithTone v={cr.status} map={STATUS_TONE} /></td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/change-requests/${cr.id}`}
                    data-testid={`admin-cr-open-${cr.id}`}
                    className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold"
                  >
                    Review →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminChangeRequestDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [cr, setCr] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const canApprove = ["platform_owner", "platform_admin"].includes(me?.admin_role);
  const canWithdraw = cr && cr.status === "pending" &&
    (me?.admin_role === "platform_owner" || me?.id === cr.proposer_id);

  const load = () => {
    api.get(`/admin/change-requests/${cid}`).then((r) => setCr(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load();   }, [cid]);

  const act = async (path) => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/change-requests/${cid}/${path}`, { notes: notes.trim() });
      setCr(r.data);
      toast.success(`Change ${r.data.status}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!cr) return <div className="p-6 text-[13px] text-[#667085]">Loading change request…</div>;

  return (
    <div className="space-y-5" data-testid="admin-cr-detail-root">
      <button
        onClick={() => nav("/admin/change-requests")}
        data-testid="admin-cr-back"
        className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1"
      >
        <ArrowLeft size={13} /> Back to change requests
      </button>

      <div className="bg-white rounded-xl border border-[#E4E7EC] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Change Request</div>
            <div className="flex items-center gap-2 flex-wrap">
              <PillWithTone v={cr.action} map={ACTION_TONE} />
              <PillWithTone v={cr.status} map={STATUS_TONE} />
              <span className="text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">
                {cr.entity_type.replace(/_/g, " ")}
              </span>
            </div>
            <div className="text-[11px] text-[#667085] font-mono mt-2">CR ID: {cr.id}</div>
            <div className="text-[11px] text-[#667085] font-mono mt-0.5">Created: {fmtDateTime(cr.created_at)}</div>
            {cr.reviewed_at && <div className="text-[11px] text-[#667085] font-mono mt-0.5">Reviewed: {fmtDateTime(cr.reviewed_at)} by {cr.reviewer_email}</div>}
          </div>
        </div>

        {cr.justification && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Proposer&apos;s justification</div>
            <div className="mt-1 text-[13px] text-[#1d3557] whitespace-pre-wrap bg-[#F7F9FC] rounded-md p-3 border border-[#E4E7EC]">
              {cr.justification}
            </div>
          </div>
        )}
        {cr.reviewer_notes && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Reviewer notes</div>
            <div className="mt-1 text-[13px] text-[#1d3557] whitespace-pre-wrap bg-[#F1FAEE] rounded-md p-3 border border-[#F1FAEE]">
              {cr.reviewer_notes}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-cr-current">
          <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
            <FileCode size={12} /> Current state
          </div>
          <pre className="mt-2 text-[11px] leading-relaxed font-mono text-[#1d3557] bg-[#F7F9FC] rounded-md p-3 border border-[#E4E7EC] overflow-auto max-h-72">
            {cr.action === "create" ? "(doesn't exist yet)" : JSON.stringify(cr.current || {}, null, 2)}
          </pre>
        </div>
        <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-cr-proposed">
          <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
            <FileCode size={12} /> Proposed change
          </div>
          <pre className="mt-2 text-[11px] leading-relaxed font-mono text-[#1d3557] bg-[#F1FAEE]/50 rounded-md p-3 border border-[#F1FAEE] overflow-auto max-h-72">
            {JSON.stringify({ action: cr.action, target_id: cr.target_id, payload: cr.payload }, null, 2)}
          </pre>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-cr-proposer">
        <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
          <User size={12} /> Proposed by
        </div>
        <div className="text-[13px] font-semibold text-[#1d3557] mt-1.5">{cr.proposer_name || cr.proposer_email}</div>
        <div className="text-[11px] text-[#667085] mt-0.5 font-mono">{cr.proposer_email}</div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#457b9d] mt-1">
          {cr.proposer_admin_role?.replace(/_/g, " ")}
        </div>
      </div>

      {/* Actions */}
      {(canApprove || canWithdraw) && cr.status === "pending" && (
        <div className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="admin-cr-actions">
          <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold flex items-center gap-1.5">
            <ShieldCheck size={12} /> Review
          </div>
          <Textarea
            rows={2}
            data-testid="admin-cr-notes"
            placeholder="Optional notes (visible to the proposer)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-2 rounded-md"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {canApprove && (
              <>
                <Button
                  onClick={() => act("approve")}
                  disabled={saving}
                  data-testid="admin-cr-approve"
                  className="h-9 bg-[#12B76A] hover:bg-[#0f9f5a] rounded-md text-white font-semibold text-[13px] px-3"
                >
                  <Check size={14} className="mr-1.5" /> Approve & publish
                </Button>
                <Button
                  onClick={() => act("reject")}
                  disabled={saving}
                  data-testid="admin-cr-reject"
                  className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
                >
                  <X size={14} className="mr-1.5" /> Reject
                </Button>
              </>
            )}
            {canWithdraw && (
              <Button
                onClick={() => act("withdraw")}
                disabled={saving}
                data-testid="admin-cr-withdraw"
                className="h-9 bg-white border border-[#E4E7EC] hover:border-[#e63946] text-[#e63946] rounded-md font-semibold text-[13px] px-3"
              >
                <Undo2 size={14} className="mr-1.5" /> Withdraw
              </Button>
            )}
          </div>
        </div>
      )}
      {cr.status !== "pending" && (
        <div className="text-[13px] text-[#667085] bg-[#F7F9FC] rounded-md p-3 border border-[#E4E7EC] inline-flex items-center gap-2">
          <XCircle size={13} />
          This change request is <strong className="text-[#1d3557]">{cr.status}</strong> and can no longer be modified.
        </div>
      )}
    </div>
  );
}

export default AdminChangeRequests;
